// ============================================================================
// S3 Direct Upload Service — handles pre-signed URL uploads with progress
// ============================================================================

import { api } from './api';
import type {
  UploadProgress,
  CaptionSettings,
  UploadStitchGroupMetadata,
} from '../types';

export type UploadProgressCallback = (progress: UploadProgress) => void;

export async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress: (progressPercent: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed — network error')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
}

function getMediaType(file: File): 'video' | 'image' {
  if (file.type.startsWith('video/')) return 'video';
  return 'image';
}

/**
 * Upload a file directly to S3 using a pre-signed URL.
 * Reports progress via callback.
 */
export async function uploadFile(
  file: File,
  title: string,
  description: string,
  onProgress: UploadProgressCallback,
  captionSettings?: CaptionSettings,
  stitchGroup?: UploadStitchGroupMetadata,
): Promise<string> {
  const progress: UploadProgress = {
    file,
    progress: 0,
    status: 'pending',
  };

  try {
    progress.status = 'uploading';
    progress.progress = 0;
    onProgress({ ...progress });

    const { uploadUrl, s3Key } = await api.generateUploadUrl(
      file.name,
      file.type,
      file.size,
    );
    progress.s3Key = s3Key;

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          progress.progress = Math.round((event.loaded / event.total) * 100);
          onProgress({ ...progress });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed — network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload cancelled'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });

    progress.status = 'registering';
    progress.progress = 100;
    onProgress({ ...progress });

    const metadata: Record<string, unknown> = {};
    if (captionSettings) {
      metadata.captionSettings = captionSettings;
    }
    if (stitchGroup?.enabled) {
      metadata.stitchGroup = stitchGroup;
    }

    const content = await api.createContent({
      title: title || file.name.replace(/\.[^.]+$/, ''),
      description,
      s3Key,
      mediaType: getMediaType(file),
      filename: file.name,
      fileSize: file.size,
      ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
    });

    progress.status = 'complete';
    progress.contentId = content.contentId;
    onProgress({ ...progress });

    return content.contentId;
  } catch (error) {
    progress.status = 'error';
    progress.error = error instanceof Error ? error.message : 'Upload failed';
    onProgress({ ...progress });
    throw error;
  }
}

/**
 * Upload multiple files with individual progress tracking.
 */
export async function uploadFiles(
  files: Array<{ file: File; title: string; description: string }>,
  onProgress: (fileIndex: number, progress: UploadProgress) => void,
): Promise<string[]> {
  const results: string[] = [];

  for (let i = 0; i < files.length; i++) {
    const { file, title, description } = files[i]!;
    try {
      const contentId = await uploadFile(
        file,
        title,
        description,
        (progress) => onProgress(i, progress),
      );
      results.push(contentId);
    } catch (error) {
      console.error(`Upload failed for file ${i} (${file.name}):`, error);
    }
  }

  return results;
}
